/**
 * Follow-ups — status label → colour category (redesign Phase 0, 2026-10-02).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * The design colours a status dot by one of six categories (the export's CAT / CATC maps):
 *   none   not contacted yet                         grey
 *   tried  attempted, no answer yet                  blue
 *   wait   waiting on the patient                    amber
 *   claim  claim/pre-auth work in progress           purple
 *   done   resolved (appointment, claim received…)   green
 *   stop   do not follow up                          red
 * The hex values live in src/pages/Followups/_tokens.scss ($fu-cat-*).
 *
 * ── WHY THE LOOKUP IS FORGIVING ────────────────────────────────────────────────────────────────
 * A row's Status is NOT a fixed vocabulary: it is the office's own Open Dental definition name,
 * mapped per site in teamio.client-followupstatuses-mapping (ClientSystemFollowupStatus), with the
 * queries' `IFNULL(…, 'Not Called')` for a row with no task. Real sites store "Not Home, Call Again",
 * "Wait. See notes.", "Bad Debt. Don't Call", "Patient Will Call Us" — the export writes
 * "Not home. Call again". So labels are compared after normalising case, apostrophes and
 * punctuation, and a label nobody listed falls back to the row's TeamioFollowupStatusID — the
 * bucket the office put that status in under Settings (customer.js saveSettings):
 *   1 = appointment scheduled · 5 = claim processed · 6 = requires follow-up · 3 = do not follow up.
 * TeamioFollowupStatusID 3 always wins: the legacy cell prints "Do Not Follow-up" for it whatever
 * the label says, and a green or blue dot beside that badge would contradict it.
 */

export const STATUS_CATEGORIES = ['none', 'tried', 'wait', 'claim', 'done', 'stop'];

/** The export's CAT map, verbatim (labels as the export spells them). */
export const STATUS_CATEGORY_BY_LABEL = {
  'Not called': 'none',
  'Not home. Call again': 'tried',
  'Left voicemail': 'tried',
  "Couldn't leave voicemail": 'tried',
  'Left message with fam': 'tried',
  'Texted': 'tried',
  'Emailed': 'tried',
  'Payment link sent': 'tried',
  'Patient will call us': 'wait',
  'Call back, not ready': 'wait',
  'Wait. See notes': 'wait',
  'Needs more info': 'claim',
  'Appealed': 'claim',
  'Did not receive - resend': 'claim',
  'Processed - check is in mail': 'claim',
  'Processed - check received, but not entered in OD': 'claim',
  'Appointment scheduled': 'done',
  'Claim received': 'done',
  'Payment plan set up': 'done',
  'Apt on schedule': 'done',
  "Bad debt. Don't call": 'stop',
  'Patient asked not to be called': 'stop',
  'No need to follow up': 'stop',
};

/** Lower-case, apostrophes dropped, every other non-alphanumeric run → one space. */
export function normalizeStatus(label) {
  if (label === null || label === undefined) return '';
  return String(label)
    .toLowerCase()
    .replace(/['‘’`]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const NORMALIZED = {};
Object.keys(STATUS_CATEGORY_BY_LABEL).forEach((label) => {
  NORMALIZED[normalizeStatus(label)] = STATUS_CATEGORY_BY_LABEL[label];
});

const BY_TEAMIO_STATUS_ID = { 1: 'done', 5: 'done', 6: 'tried', 3: 'stop' };

/**
 * @param {string} label                      the row's status label (Status, or 'Apt on Schedule')
 * @param {Object} [opts]
 * @param {number} [opts.teamioFollowupStatusID]  the row's TeamioFollowupStatusID
 * @returns {'none'|'tried'|'wait'|'claim'|'done'|'stop'}
 */
export function statusCategory(label, opts) {
  const id = opts && opts.teamioFollowupStatusID !== undefined && opts.teamioFollowupStatusID !== null
    ? Number(opts.teamioFollowupStatusID)
    : null;
  if (id === 3) return 'stop';
  const key = normalizeStatus(label);
  if (!key) return 'none';
  if (NORMALIZED[key]) return NORMALIZED[key];
  if (id !== null && BY_TEAMIO_STATUS_ID[id]) return BY_TEAMIO_STATUS_ID[id];
  return 'none';
}
